import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from "recharts";

import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Checkbox,
  ListItemText,
  Avatar,
  Chip,
  Skeleton,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Button,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Collapse,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import DirectionsRunOutlinedIcon from "@mui/icons-material/DirectionsRunOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";

import { getProject } from "../services/projectService";
import { getSprints, createSprint, updateSprint, deleteSprint } from "../services/sprintService";
import { getTasks, createTask, updateTask, updateTaskStatus, deleteTask } from "../services/taskService";
import { useUserRole } from "../context/UserRoleContext";
import { USER_ROLES, canWritePage } from "../auth/roles";

import TaskDialog from "../components/board/TaskDialog";
import SprintDialog from "../components/board/SprintDialog";

const priorityStyle = (priority) => {
  switch (priority) {
    case "Critical":
      return { background: "#fee2e2", color: "#dc2626" };
    case "High":
      return { background: "#ffedd5", color: "#ea580c" };
    case "Medium":
      return { background: "#fef3c7", color: "#ca8a04" };
    case "Low":
      return { background: "#dcfce7", color: "#16a34a" };
    default:
      return { background: "#f1f5f9", color: "#475569" };
  }
};

const statusAccent = (status) => {
  switch (status) {
    case "To Do":
      return "#94a3b8";
    case "In Progress":
      return "#7c3aed";
    case "Done":
      return "#16a34a";
    default:
      return "#94a3b8";
  }
};

const TASK_TYPE_ICON = {
  Task: <TaskAltOutlinedIcon sx={{ fontSize: 15, color: "#2563eb" }} />,
  Bug: <BugReportOutlinedIcon sx={{ fontSize: 15, color: "#dc2626" }} />,
  Story: <AutoStoriesOutlinedIcon sx={{ fontSize: 15, color: "#16a34a" }} />,
};

const PRIORITY_ICON = {
  Low: <KeyboardDoubleArrowDownIcon sx={{ fontSize: 14 }} />,
  Medium: <DragHandleIcon sx={{ fontSize: 14 }} />,
  High: <KeyboardDoubleArrowUpIcon sx={{ fontSize: 14 }} />,
  Critical: <PriorityHighIcon sx={{ fontSize: 14 }} />,
};

const STATUS_COLUMNS = ["To Do", "In Progress", "Done"];
const BOARD_PRIORITIES = ["Low", "Medium", "High", "Critical"];
const VISIBLE_ASSIGNEE_COUNT = 5;
const CALENDAR_MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const CALENDAR_DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const sprintTimelineColor = (status) => {
  switch (status) {
    case "active":
      return "#7c3aed";
    case "paused":
      return "#ea580c";
    case "completed":
      return "#16a34a";
    default:
      return "#94a3b8";
  }
};

const initialsOf = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const AVATAR_PALETTE = ["#2563eb", "#dc2626", "#16a34a", "#ca8a04", "#7c3aed", "#db2777", "#0891b2", "#ea580c", "#4f46e5", "#059669", "#be123c", "#0d9488"];

// Gives each user a consistent, distinct avatar color instead of one shared color.
const avatarColorOf = (key) => {
  const str = String(key ?? "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const formatDateRange = (start, end) => {
  if (!start && !end) return "";
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "?");
  return `${fmt(start)} - ${fmt(end)}`;
};

function TaskRow({ task, onEdit, onDelete, canManageBoard = true }) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  return (
    <Box
      onClick={() => onEdit(task)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.2,
        px: 1.5,
        py: 1,
        pl: task.parent_task_id ? 4 : 1.5,
        borderRadius: 1.5,
        cursor: "pointer",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {task.parent_task_id && (
        <SubdirectoryArrowRightIcon sx={{ fontSize: 15, color: "text.secondary" }} />
      )}

      {TASK_TYPE_ICON[task.task_type] || TASK_TYPE_ICON.Task}

      <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600, minWidth: 30 }}>
        #{task.task_id}
      </Typography>

      <Typography sx={{ fontSize: 13, color: "text.primary", flex: 1, fontWeight: 500 }} noWrap>
        {task.title}
      </Typography>

      {task.subtask_count > 0 && (
        <Chip
          label={`${task.subtask_done_count}/${task.subtask_count}`}
          size="small"
          sx={{ height: 20, fontSize: 10, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
        />
      )}

      {task.story_points != null && (
        <Chip label={`${task.story_points} pt`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }} />
      )}

      <Chip
        icon={PRIORITY_ICON[task.priority]}
        label={task.priority}
        size="small"
        sx={{ fontSize: 10, fontWeight: 600, height: 20, ...priorityStyle(task.priority) }}
      />

      <Chip
        label={task.status}
        size="small"
        sx={{ fontSize: 10, fontWeight: 600, height: 20, bgcolor: "background.paper", color: statusAccent(task.status), border: "1px solid", borderColor: "divider" }}
      />

      <Tooltip title={task.assigned_name || "Unassigned"}>
        <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: avatarColorOf(task.assigned_to ?? task.assigned_name) }}>
          {task.assigned_name ? initialsOf(task.assigned_name) : "?"}
        </Avatar>
      </Tooltip>

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setMenuAnchor(e.currentTarget);
        }}
        sx={{ visibility: canManageBoard ? "visible" : "hidden" }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(null);
            onEdit(task);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(null);
            onDelete(task);
          }}
          sx={{ color: "#dc2626" }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}

function BoardCard({ task, index, onEdit, canDrag = true }) {
  return (
    <Draggable draggableId={String(task.task_id)} index={index} isDragDisabled={!canDrag}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onEdit(task)}
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: snapshot.isDragging ? "primary.main" : "divider",
            borderRadius: 2,
            p: 1.4,
            mb: 1.1,
            cursor: "pointer",
            boxShadow: snapshot.isDragging ? "0 8px 20px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
            "&:hover": { borderColor: "primary.main" },
          }}
        >
          <Box display="flex" alignItems="center" gap={0.7} mb={0.6}>
            {TASK_TYPE_ICON[task.task_type] || TASK_TYPE_ICON.Task}
            <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>
              #{task.task_id}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "text.primary",
              mb: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {task.title}
          </Typography>

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" gap={0.5}>
              <Chip icon={PRIORITY_ICON[task.priority]} label={task.priority} size="small" sx={{ fontSize: 10, height: 18, ...priorityStyle(task.priority) }} />
              {task.story_points != null && (
                <Chip label={`${task.story_points} pt`} size="small" sx={{ height: 18, fontSize: 10 }} />
              )}
            </Box>

            <Tooltip title={task.assigned_name || "Unassigned"}>
              <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: avatarColorOf(task.assigned_to ?? task.assigned_name) }}>
                {task.assigned_name ? initialsOf(task.assigned_name) : "?"}
              </Avatar>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Draggable>
  );
}

function SummaryStatCard({ label, value, accent }) {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        flex: 1,
        minWidth: 140,
      }}
    >
      <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, color: accent || "text.primary" }}>
        {value}
      </Typography>
    </Box>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 7,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          mb: 1.5,
        }}
      >
        <Icon sx={{ fontSize: 30, color: "primary.main" }} />
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: "text.primary", mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 13, color: "text.secondary", maxWidth: 340 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

const TASK_TABLE_HEADERS = ["Type", "ID", "Title", "Priority", "Status", "Points", "Assignee"];

function TaskTable({ tasks, onEdit, onDelete, canManageBoard = true }) {
  const [page, setPage] = useState(1);
  const rowsPerPage = 50;

  const startIndex = (page - 1) * rowsPerPage;
  const paginatedTasks = tasks.slice(startIndex, startIndex + rowsPerPage);
  const totalPages = Math.ceil(tasks.length / rowsPerPage);
  const tableHeaders = canManageBoard ? [...TASK_TABLE_HEADERS, "Actions"] : TASK_TABLE_HEADERS;

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              {tableHeaders.map((head) => (
                <TableCell
                  key={head}
                  sx={{ fontWeight: 600, fontSize: 13, color: "text.secondary" }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedTasks.map((task) => (
              <TableRow
                key={task.task_id}
                hover
                onClick={() => onEdit(task)}
                sx={{ cursor: "pointer", "& td": { borderBottom: "1px solid", borderColor: "divider" } }}
              >
                <TableCell>{TASK_TYPE_ICON[task.task_type] || TASK_TYPE_ICON.Task}</TableCell>

                <TableCell>
                  <Typography fontSize={13} color="text.secondary" fontWeight={600}>
                    #{task.task_id}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {task.parent_task_id && (
                      <SubdirectoryArrowRightIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                    )}
                    <Typography fontSize={13} fontWeight={500} color="text.primary">
                      {task.title}
                    </Typography>
                    {task.subtask_count > 0 && (
                      <Chip
                        label={`${task.subtask_done_count}/${task.subtask_count}`}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}
                      />
                    )}
                  </Box>
                </TableCell>

                <TableCell>
                  <Chip icon={PRIORITY_ICON[task.priority]} label={task.priority} size="small" sx={{ fontSize: 11, fontWeight: 600, height: 22, ...priorityStyle(task.priority) }} />
                </TableCell>

                <TableCell>
                  <Chip
                    label={task.status}
                    size="small"
                    sx={{ fontSize: 11, fontWeight: 600, height: 22, bgcolor: "background.default", color: statusAccent(task.status), border: "1px solid", borderColor: "divider" }}
                  />
                </TableCell>

                <TableCell>
                  <Typography fontSize={13} color="text.secondary">
                    {task.story_points ?? "-"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: avatarColorOf(task.assigned_to ?? task.assigned_name) }}>
                      {task.assigned_name ? initialsOf(task.assigned_name) : "?"}
                    </Avatar>
                    <Typography fontSize={13} color="text.primary" noWrap>
                      {task.assigned_name || "Unassigned"}
                    </Typography>
                  </Box>
                </TableCell>

                {canManageBoard && (
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(task);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" sx={{ color: "#dc2626" }} />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}

            {paginatedTasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={tableHeaders.length} align="center">
                  <Typography sx={{ fontSize: 13, color: "text.secondary", py: 3 }}>
                    No tasks found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {tasks.length > 0 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography fontSize={13} color="text.secondary">
            Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, tasks.length)} of {tasks.length} results
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Button size="small" disabled={page === 1} onClick={() => setPage(page - 1)}>
              <ChevronLeftIcon />
            </Button>

            <Button variant="contained" size="small" sx={{ minWidth: 32 }}>
              {page}
            </Button>

            <Button size="small" disabled={page === totalPages || totalPages === 0} onClick={() => setPage(page + 1)}>
              <ChevronRightIcon />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ProjectBoard() {
  const { projectId: selectedProjectId, taskId: urlTaskId } = useParams();
  const navigate = useNavigate();
  const { role, employee } = useUserRole();

  const [projectName, setProjectName] = useState("");
  const [projectTeam, setProjectTeam] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(() => Boolean(selectedProjectId));
  const [activeTab, setActiveTab] = useState("summary");
  const [expandedSprints, setExpandedSprints] = useState({});

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [defaultSprintId, setDefaultSprintId] = useState(null);
  const [sprintDialogOpen, setSprintDialogOpen] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: "task"|"sprint", item }
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadBoardData = useCallback(async () => {
    if (!selectedProjectId) {
      return;
    }

    try {
      setLoading(true);
      const [project, sprintData, taskData] = await Promise.all([
        getProject(selectedProjectId),
        getSprints(selectedProjectId),
        getTasks(selectedProjectId),
      ]);

      setProjectName(project.project_name || "");

      const team = Array.isArray(project.assigned_to) ? [...project.assigned_to] : [];
      if (project.project_manager_id && !team.some((m) => m.employee_id === project.project_manager_id)) {
        team.push({ employee_id: project.project_manager_id, employee_name: project.project_manager_name });
      }
      // Logged-in user shows up first in assignee pickers/filters.
      team.sort((a, b) => {
        const aIsSelf = a.employee_id === employee?.employee_id ? 0 : 1;
        const bIsSelf = b.employee_id === employee?.employee_id ? 0 : 1;
        return aIsSelf - bIsSelf;
      });
      setProjectTeam(team);

      setSprints(sprintData);
      setTasks(taskData);
      setExpandedSprints((prev) => {
        const next = { ...prev };
        sprintData.forEach((sprint) => {
          if (!(sprint.sprint_id in next)) {
            next[sprint.sprint_id] = sprint.status === "active";
          }
        });
        return next;
      });
    } catch (error) {
      console.error("Board Load Error:", error.response?.data || error);
      setSnackbar({ open: true, message: "Failed to load the board", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, employee?.employee_id]);

  useEffect(() => {
    loadBoardData();
  }, [loadBoardData]);

  // Deep-linking: opening /board/:projectId/task/:taskId directly (or refreshing
  // on it) should reopen that task's dialog once its data has loaded.
  useEffect(() => {
    if (!urlTaskId || tasks.length === 0) {
      return;
    }
    const targetTask = tasks.find((t) => String(t.task_id) === String(urlTaskId));
    if (targetTask && (!editingTask || editingTask.task_id !== targetTask.task_id)) {
      setEditingTask(targetTask);
      setDefaultSprintId(null);
      setTaskDialogOpen(true);
    }
  }, [urlTaskId, tasks, editingTask]);

  const projectTeamIds = useMemo(() => projectTeam.map((m) => m.employee_id), [projectTeam]);

  const isProjectMember =
    role === USER_ROLES.SUPER_ADMIN || projectTeamIds.includes(employee?.employee_id);

  const canManageBoard = canWritePage(role, "board") && isProjectMember;

  // Even with read-only board access, project team members can still create tasks
  // and drag them between status columns (edit/delete/sprint-management stays
  // gated behind canManageBoard).
  const canCreateTasks = isProjectMember;

  const backlogTasks = useMemo(() => tasks.filter((t) => !t.sprint_id), [tasks]);
  const tasksBySprint = useMemo(() => {
    const map = {};
    tasks.forEach((task) => {
      if (task.sprint_id) {
        map[task.sprint_id] = map[task.sprint_id] || [];
        map[task.sprint_id].push(task);
      }
    });
    return map;
  }, [tasks]);

  const activeSprint = useMemo(() => sprints.find((s) => s.status === "active") || null, [sprints]);

  const [boardSearch, setBoardSearch] = useState("");
  const [boardAssigneeFilters, setBoardAssigneeFilters] = useState([]);
  const [boardPriorityFilters, setBoardPriorityFilters] = useState([]);
  const [assigneeMenuAnchor, setAssigneeMenuAnchor] = useState(null);

  const toggleBoardAssigneeFilter = (employeeId) => {
    setBoardAssigneeFilters((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
    );
  };

  const matchesBoardFilters = useCallback(
    (task) => {
      const search = boardSearch.trim().toLowerCase();
      if (search && !task.title.toLowerCase().includes(search)) {
        return false;
      }
      if (boardAssigneeFilters.length > 0 && !boardAssigneeFilters.includes(task.assigned_to)) {
        return false;
      }
      if (boardPriorityFilters.length > 0 && !boardPriorityFilters.includes(task.priority)) {
        return false;
      }
      return true;
    },
    [boardSearch, boardAssigneeFilters, boardPriorityFilters]
  );

  const hasActiveBoardFilters = Boolean(
    boardSearch.trim() || boardAssigneeFilters.length > 0 || boardPriorityFilters.length > 0
  );

  // Applied everywhere (Summary, Sprints, Backlog, Board, List, Timeline, Calendar) so the filter bar has one consistent effect.
  const filteredTasks = useMemo(() => tasks.filter(matchesBoardFilters), [tasks, matchesBoardFilters]);
  const filteredBacklogTasks = useMemo(() => backlogTasks.filter(matchesBoardFilters), [backlogTasks, matchesBoardFilters]);

  const activeSprintTasks = useMemo(
    () => (activeSprint ? (tasksBySprint[activeSprint.sprint_id] || []).filter(matchesBoardFilters) : []),
    [activeSprint, tasksBySprint, matchesBoardFilters]
  );

  // Board tab shows only the active (in-progress) sprint's kanban, not every sprint.
  const boardSprintSections = useMemo(
    () =>
      sprints
        .filter((sprint) => sprint.status === "active")
        .map((sprint) => {
          const sprintTasks = (tasksBySprint[sprint.sprint_id] || []).filter(matchesBoardFilters);
          return {
            sprint,
            taskCount: sprintTasks.length,
            columns: STATUS_COLUMNS.map((status) => ({
              status,
              tasks: sprintTasks.filter((t) => t.status === status),
            })),
          };
        }),
    [sprints, tasksBySprint, matchesBoardFilters]
  );

  // Timeline tab: a Gantt-style bar per sprint across its own start/end dates.
  const timelineData = useMemo(() => {
    const sprintsWithDates = sprints.filter((s) => s.start_date && s.end_date);
    if (sprintsWithDates.length === 0) {
      return null;
    }

    const rangeStart = Math.min(...sprintsWithDates.map((s) => new Date(s.start_date).getTime()));
    const rangeEnd = Math.max(...sprintsWithDates.map((s) => new Date(s.end_date).getTime()));
    const totalSpan = Math.max(rangeEnd - rangeStart, 24 * 60 * 60 * 1000);

    const rows = sprintsWithDates
      .slice()
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .map((sprint) => {
        const start = new Date(sprint.start_date).getTime();
        const end = new Date(sprint.end_date).getTime();
        return {
          sprint,
          leftPct: ((start - rangeStart) / totalSpan) * 100,
          widthPct: Math.max(((end - start) / totalSpan) * 100, 2),
          taskCount: (tasksBySprint[sprint.sprint_id] || []).filter(matchesBoardFilters).length,
        };
      });

    return { rangeStart, rangeEnd, rows };
  }, [sprints, tasksBySprint, matchesBoardFilters]);

  // Calendar tab
  const [calendarMonth, setCalendarMonth] = useState(() => new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(() => new Date().getFullYear());
  const [calendarPopover, setCalendarPopover] = useState(null);

  const shiftCalendarMonth = (delta) => {
    const next = new Date(calendarYear, calendarMonth + delta, 1);
    setCalendarMonth(next.getMonth());
    setCalendarYear(next.getFullYear());
  };

  const calendarCells = useMemo(() => {
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    const firstWeekday = new Date(calendarYear, calendarMonth, 1).getDay();
    const cells = [];

    for (let i = 0; i < firstWeekday; i++) {
      cells.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarYear, calendarMonth, day);
      const daySprints = sprints
        .filter((s) => s.start_date && s.end_date)
        .filter((s) => {
          const start = new Date(s.start_date);
          const end = new Date(s.end_date);
          const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
          return date >= startDay && date <= endDay;
        })
        .map((sprint) => ({
          sprint,
          color: sprintTimelineColor(sprint.status),
          taskCount: (tasksBySprint[sprint.sprint_id] || []).filter(matchesBoardFilters).length,
        }));
      cells.push({ day, date, daySprints });
    }

    return cells;
  }, [calendarMonth, calendarYear, sprints, tasksBySprint, matchesBoardFilters]);

  const openCalendarDay = (event, cell) => {
    setCalendarPopover({
      anchorEl: event.currentTarget,
      dateLabel: cell.date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }),
      sprints: cell.daySprints,
    });
  };

  // Summary tab (respects the shared board filters, same as every other tab)
  const summaryStats = useMemo(() => {
    const byStatus = { "To Do": 0, "In Progress": 0, "Done": 0 };
    const byType = { Task: 0, Bug: 0, Story: 0 };
    const byPriority = { Low: 0, Medium: 0, High: 0, Critical: 0 };
    const byAssignee = {};

    filteredTasks.forEach((task) => {
      byStatus[task.status] = (byStatus[task.status] || 0) + 1;
      byType[task.task_type] = (byType[task.task_type] || 0) + 1;
      byPriority[task.priority] = (byPriority[task.priority] || 0) + 1;
      const key = task.assigned_name || "Unassigned";
      byAssignee[key] = (byAssignee[key] || 0) + 1;
    });

    return {
      total: filteredTasks.length,
      byStatus,
      byType,
      byPriority,
      byAssignee,
      statusChartData: Object.entries(byStatus).map(([status, count]) => ({ name: status, value: count })),
      priorityChartData: Object.entries(byPriority).map(([priority, count]) => ({ name: priority, value: count })),
    };
  }, [filteredTasks]);

  // List tab (flat view of every task, across all sprints + backlog)
  const [listStatusFilter, setListStatusFilter] = useState("");

  const filteredListTasks = useMemo(
    () => filteredTasks.filter((task) => !listStatusFilter || task.status === listStatusFilter),
    [filteredTasks, listStatusFilter]
  );

  const toggleSprintExpanded = (sprintId) => {
    setExpandedSprints((prev) => ({ ...prev, [sprintId]: !prev[sprintId] }));
  };

  const openCreateTask = (sprintId = null) => {
    setEditingTask(null);
    setDefaultSprintId(sprintId);
    setTaskDialogOpen(true);
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setDefaultSprintId(null);
    setTaskDialogOpen(true);
    navigate(`/board/${selectedProjectId}/task/${task.task_id}`);
  };

  const closeTaskDialog = () => {
    setTaskDialogOpen(false);
    if (urlTaskId) {
      navigate(`/board/${selectedProjectId}`);
    }
  };

  const handleSaveTask = async (data) => {
    try {
      setIsSaving(true);
      if (editingTask) {
        await updateTask(editingTask.task_id, { ...data, parent_task_id: editingTask.parent_task_id ?? null });
        setSnackbar({ open: true, message: "Task updated successfully", severity: "success" });
      } else {
        await createTask({ ...data, project_id: Number(selectedProjectId), created_by: employee?.employee_id });
        setSnackbar({ open: true, message: "Task created successfully", severity: "success" });
      }
      setTaskDialogOpen(false);
      if (urlTaskId) {
        navigate(`/board/${selectedProjectId}`);
      }
      await loadBoardData();
    } catch (error) {
      console.error("Task Save Error:", error.response?.data || error);
      setSnackbar({ open: true, message: error?.response?.data?.detail || "Failed to save task", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateSubtask = async (parentTask, title) => {
    try {
      await createTask({
        project_id: parentTask.project_id,
        sprint_id: parentTask.sprint_id,
        parent_task_id: parentTask.task_id,
        title,
        task_type: "Task",
        priority: "Medium",
        created_by: employee?.employee_id,
      });
      setSnackbar({ open: true, message: "Subtask created successfully", severity: "success" });
      await loadBoardData();
    } catch (error) {
      console.error("Subtask Create Error:", error.response?.data || error);
      setSnackbar({ open: true, message: error?.response?.data?.detail || "Failed to create subtask", severity: "error" });
    }
  };

  const openCreateSprint = () => {
    setEditingSprint(null);
    setSprintDialogOpen(true);
  };

  const openEditSprint = (sprint) => {
    setEditingSprint(sprint);
    setSprintDialogOpen(true);
  };

  const handleSaveSprint = async (data) => {
    try {
      setIsSaving(true);
      if (editingSprint) {
        await updateSprint(editingSprint.sprint_id, data);
        setSnackbar({ open: true, message: "Sprint updated successfully", severity: "success" });
      } else {
        await createSprint({ ...data, project_id: Number(selectedProjectId) });
        setSnackbar({ open: true, message: "Sprint created successfully", severity: "success" });
      }
      setSprintDialogOpen(false);
      await loadBoardData();
    } catch (error) {
      console.error("Sprint Save Error:", error.response?.data || error);
      setSnackbar({ open: true, message: error?.response?.data?.detail || "Failed to save sprint", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const [sprintActionPendingId, setSprintActionPendingId] = useState(null);

  const handleStartSprint = async (sprint) => {
    if (sprintActionPendingId) {
      return;
    }
    setSprintActionPendingId(sprint.sprint_id);
    try {
      await updateSprint(sprint.sprint_id, {
        sprint_name: sprint.sprint_name,
        goal: sprint.goal,
        start_date: sprint.start_date,
        end_date: sprint.end_date,
        status: "active",
      });
      setSnackbar({ open: true, message: `${sprint.sprint_name} started`, severity: "success" });
      setActiveTab("board");
      await loadBoardData();
    } catch (error) {
      console.error("Start Sprint Error:", error.response?.data || error);
      setSnackbar({ open: true, message: error?.response?.data?.detail || "Failed to start sprint", severity: "error" });
    } finally {
      setSprintActionPendingId(null);
    }
  };

  const handleCompleteSprint = async (sprint) => {
    if (sprintActionPendingId) {
      return;
    }
    setSprintActionPendingId(sprint.sprint_id);
    try {
      await updateSprint(sprint.sprint_id, {
        sprint_name: sprint.sprint_name,
        goal: sprint.goal,
        start_date: sprint.start_date,
        end_date: sprint.end_date,
        status: "completed",
      });
      setSnackbar({ open: true, message: `${sprint.sprint_name} completed`, severity: "success" });
      await loadBoardData();
    } catch (error) {
      console.error("Complete Sprint Error:", error.response?.data || error);
      setSnackbar({ open: true, message: error?.response?.data?.detail || "Failed to complete sprint", severity: "error" });
    } finally {
      setSprintActionPendingId(null);
    }
  };

  const handleReopenSprint = async (sprint) => {
    if (sprintActionPendingId) {
      return;
    }
    setSprintActionPendingId(sprint.sprint_id);
    try {
      await updateSprint(sprint.sprint_id, {
        sprint_name: sprint.sprint_name,
        goal: sprint.goal,
        start_date: sprint.start_date,
        end_date: sprint.end_date,
        status: "active",
      });
      setSnackbar({ open: true, message: `${sprint.sprint_name} reopened`, severity: "success" });
      setActiveTab("board");
      await loadBoardData();
    } catch (error) {
      console.error("Reopen Sprint Error:", error.response?.data || error);
      setSnackbar({ open: true, message: error?.response?.data?.detail || "Failed to reopen sprint", severity: "error" });
    } finally {
      setSprintActionPendingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;

    setIsDeleting(true);
    try {
      if (deleteTarget.type === "task") {
        await deleteTask(deleteTarget.item.task_id);
        setSnackbar({ open: true, message: "Task deleted", severity: "success" });
      } else {
        await deleteSprint(deleteTarget.item.sprint_id);
        setSnackbar({ open: true, message: "Sprint deleted", severity: "success" });
      }
      setDeleteTarget(null);
      await loadBoardData();
    } catch (error) {
      console.error("Delete Error:", error.response?.data || error);
      setSnackbar({ open: true, message: error?.response?.data?.detail || "Failed to delete", severity: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const [sourceSprintId, sourceStatus] = source.droppableId.split("::");
    const [destSprintId, destStatus] = destination.droppableId.split("::");

    // Dragging a task into a different sprint's section doesn't move it between sprints.
    if (sourceSprintId !== destSprintId || destStatus === sourceStatus) {
      return;
    }

    const taskId = Number(draggableId);
    const previousTasks = tasks;

    setTasks((prev) => prev.map((t) => (t.task_id === taskId ? { ...t, status: destStatus } : t)));

    try {
      await updateTaskStatus(taskId, destStatus);
    } catch (error) {
      console.error("Task Status Update Error:", error.response?.data || error);
      setTasks(previousTasks);
      setSnackbar({ open: true, message: "Failed to update task status", severity: "error" });
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          {selectedProjectId && loading ? (
            <>
              <Skeleton variant="text" width={220} height={40} />
              <Skeleton variant="text" width={280} height={20} />
            </>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={1}>
                <ViewKanbanOutlinedIcon sx={{ color: "text.secondary" }} />
                <Typography variant="h4" fontWeight={600} color="text.primary">
                  {projectName || "Task Board"}
                </Typography>
              </Box>
              <Typography fontSize={13} color="text.secondary" mt={0.5}>
                Plan sprints and track project tasks, Jira-style.
              </Typography>
            </>
          )}
        </Box>

        {selectedProjectId && (
          <Tooltip title="Refresh">
            <IconButton onClick={loadBoardData} sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {selectedProjectId && (
        <Tabs
          value={activeTab}
          onChange={(e, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Tab value="summary" label="Summary" icon={<InsightsOutlinedIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }} />
          <Tab value="sprints" label="Sprints" icon={<DirectionsRunOutlinedIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }} />
          <Tab value="backlog" label="Backlog" icon={<FormatListBulletedOutlinedIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }} />
          <Tab value="board" label="Board" icon={<ViewKanbanOutlinedIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }} />
          <Tab value="timeline" label="Timeline" icon={<TimelineOutlinedIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }} />
          <Tab value="calendar" label="Calendar" icon={<CalendarMonthOutlinedIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }} />
          <Tab value="list" label="List" icon={<ViewListOutlinedIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }} />
        </Tabs>
      )}

      {selectedProjectId && !loading && (
        <Box display="flex" flexWrap="wrap" gap={1.5} mb={2}>
          <TextField
            size="small"
            placeholder="Search tasks"
            value={boardSearch}
            onChange={(e) => setBoardSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
          />

          <Box
            display="flex"
            alignItems="center"
            gap={0.7}
            sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, px: 1, py: 0.5 }}
          >
            {projectTeam.slice(0, VISIBLE_ASSIGNEE_COUNT).map((member) => {
              const isSelected = boardAssigneeFilters.includes(member.employee_id);
              const memberColor = avatarColorOf(member.employee_id);
              return (
                <Tooltip key={member.employee_id} title={member.employee_name}>
                  <Avatar
                    onClick={() => toggleBoardAssigneeFilter(member.employee_id)}
                    sx={{
                      width: 30,
                      height: 30,
                      fontSize: 12,
                      cursor: "pointer",
                      bgcolor: memberColor,
                      color: "#fff",
                      border: "2px solid",
                      borderColor: isSelected ? memberColor : "transparent",
                      boxShadow: isSelected ? `0 0 0 2px ${memberColor}` : "none",
                      opacity: isSelected ? 1 : 0.5,
                      transition: "all 0.15s ease",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    {initialsOf(member.employee_name)}
                  </Avatar>
                </Tooltip>
              );
            })}

            {projectTeam.length > VISIBLE_ASSIGNEE_COUNT && (
              <>
                <Tooltip title="Show all team members">
                  <Avatar
                    onClick={(e) => setAssigneeMenuAnchor(e.currentTarget)}
                    sx={{
                      width: 30,
                      height: 30,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      bgcolor: "background.default",
                      color: "text.secondary",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    +{projectTeam.length - VISIBLE_ASSIGNEE_COUNT}
                  </Avatar>
                </Tooltip>

                <Menu
                  anchorEl={assigneeMenuAnchor}
                  open={Boolean(assigneeMenuAnchor)}
                  onClose={() => setAssigneeMenuAnchor(null)}
                >
                  {projectTeam.map((member) => (
                    <MenuItem key={member.employee_id} onClick={() => toggleBoardAssigneeFilter(member.employee_id)}>
                      <Checkbox size="small" checked={boardAssigneeFilters.includes(member.employee_id)} />
                      <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: avatarColorOf(member.employee_id), color: "#fff", mr: 1 }}>
                        {initialsOf(member.employee_name)}
                      </Avatar>
                      <ListItemText primary={member.employee_name} />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {boardAssigneeFilters.length > 0 && (
              <Tooltip title="Clear assignee filter">
                <IconButton size="small" onClick={() => setBoardAssigneeFilters([])}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>✕</Typography>
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <TextField
            select
            size="small"
            value={boardPriorityFilters}
            onChange={(e) => {
              const { value } = e.target;
              setBoardPriorityFilters(typeof value === "string" ? value.split(",") : value);
            }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) => (selected.length === 0 ? "All Priorities" : selected.join(", ")),
            }}
            sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
          >
            {BOARD_PRIORITIES.map((priority) => (
              <MenuItem key={priority} value={priority}>
                <Checkbox size="small" checked={boardPriorityFilters.includes(priority)} />
                <Box sx={{ display: "flex", alignItems: "center", mr: 0.8 }}>{PRIORITY_ICON[priority]}</Box>
                <ListItemText primary={priority} />
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {loading ? (
        <Box>
          <Skeleton variant="rounded" height={60} sx={{ mb: 1.5, borderRadius: 2 }} />
          <Skeleton variant="rounded" height={60} sx={{ mb: 1.5, borderRadius: 2 }} />
          <Skeleton variant="rounded" height={60} sx={{ borderRadius: 2 }} />
        </Box>
      ) : !selectedProjectId ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            textAlign: "center",
          }}
        >
          <ViewKanbanOutlinedIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1.5 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "text.primary", mb: 0.5 }}>
            Pick a project from the sidebar
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Expand &quot;Board&quot; in the sidebar and select a project to see its backlog and board.
          </Typography>
        </Box>
      ) : activeTab === "summary" ? (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary", mb: 1 }}>
            Overview
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1.5} mb={3}>
            <SummaryStatCard label="Total Tasks" value={summaryStats.total} />
            <SummaryStatCard label="To Do" value={summaryStats.byStatus["To Do"]} accent="#94a3b8" />
            <SummaryStatCard label="In Progress" value={summaryStats.byStatus["In Progress"]} accent="#7c3aed" />
            <SummaryStatCard label="Done" value={summaryStats.byStatus["Done"]} accent="#16a34a" />
            <SummaryStatCard label="Sprints" value={sprints.length} />
          </Box>

          {activeSprint && (
            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, mb: 3 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                Active Sprint: {activeSprint.sprint_name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {activeSprintTasks.filter((t) => t.status === "Done").length} of {activeSprintTasks.length} tasks completed
                {formatDateRange(activeSprint.start_date, activeSprint.end_date) ? ` \u00b7 ${formatDateRange(activeSprint.start_date, activeSprint.end_date)}` : ""}
              </Typography>
            </Box>
          )}

          <Box display="flex" flexWrap="wrap" gap={3} mb={3}>
            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, flex: 1, minWidth: 280 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 1 }}>
                Status Distribution
              </Typography>
              {summaryStats.total === 0 ? (
                <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", py: 4 }}>
                  No tasks yet
                </Typography>
              ) : (
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={summaryStats.statusChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={4}
                        dataKey="value"
                      >
                        {summaryStats.statusChartData.map((entry) => (
                          <Cell key={entry.name} fill={statusAccent(entry.name)} />
                        ))}
                      </Pie>
                      <ChartTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              )}
              <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1.5} mt={1}>
                {summaryStats.statusChartData.map((entry) => (
                  <Box key={entry.name} display="flex" alignItems="center" gap={0.6}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: statusAccent(entry.name) }} />
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{entry.name} ({entry.value})</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, flex: 1, minWidth: 280 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 1 }}>
                Tasks by Priority
              </Typography>
              {summaryStats.total === 0 ? (
                <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", py: 4 }}>
                  No tasks yet
                </Typography>
              ) : (
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summaryStats.priorityChartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <ChartTooltip />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {summaryStats.priorityChartData.map((entry) => (
                          <Cell key={entry.name} fill={priorityStyle(entry.name).color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 1 }}>
                By Type
              </Typography>
              {Object.entries(summaryStats.byType).map(([type, count]) => (
                <Box key={type} display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
                  <Box display="flex" alignItems="center" gap={0.8}>
                    {TASK_TYPE_ICON[type] || TASK_TYPE_ICON.Task}
                    <Typography sx={{ fontSize: 13, color: "text.primary" }}>{type}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>{count}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 1 }}>
                By Priority
              </Typography>
              {Object.entries(summaryStats.byPriority).map(([priority, count]) => (
                <Box key={priority} display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
                  <Chip icon={PRIORITY_ICON[priority]} label={priority} size="small" sx={{ fontSize: 10, height: 20, ...priorityStyle(priority) }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>{count}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 1 }}>
                Team Workload
              </Typography>
              {Object.entries(summaryStats.byAssignee).map(([name, count]) => (
                <Box key={name} display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
                  <Box display="flex" alignItems="center" gap={0.8}>
                    <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: avatarColorOf(name) }}>
                      {initialsOf(name === "Unassigned" ? "?" : name)}
                    </Avatar>
                    <Typography sx={{ fontSize: 13, color: "text.primary" }} noWrap>{name}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>{count}</Typography>
                </Box>
              ))}
              {Object.keys(summaryStats.byAssignee).length === 0 && (
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>No tasks yet</Typography>
              )}
            </Box>
          </Box>
        </Box>
      ) : activeTab === "sprints" ? (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={1.5}>
            {canManageBoard && (
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={openCreateSprint}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Create Sprint
              </Button>
            )}
          </Box>

          {sprints.map((sprint) => (
            <Box
              key={sprint.sprint_id}
              sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 1.5, overflow: "hidden" }}
            >
              <Box
                onClick={() => toggleSprintExpanded(sprint.sprint_id)}
                sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.2, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
              >
                <IconButton size="small">
                  {expandedSprints[sprint.sprint_id] ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>

                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "text.primary" }}>
                  {sprint.sprint_name}
                </Typography>

                {sprint.status === "planned" && (
                  <Chip label="Planned" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "background.default" }} />
                )}
                {sprint.status === "active" && (
                  <Chip label="In Progress" size="small" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                )}
                {sprint.status === "paused" && (
                  <Chip label="Paused" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#ffedd5", color: "#ea580c" }} />
                )}
                {sprint.status === "completed" && (
                  <Chip label="Completed" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                )}

                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  {formatDateRange(sprint.start_date, sprint.end_date)}
                </Typography>

                <Chip label={`${sprint.task_count} tasks`} size="small" sx={{ height: 20, fontSize: 10 }} />

                <Box flex={1} />

                {canManageBoard && sprint.status === "planned" && (
                  <Button
                    size="small"
                    startIcon={<PlayArrowIcon fontSize="small" />}
                    onClick={(e) => { e.stopPropagation(); handleStartSprint(sprint); }}
                    disabled={Boolean(sprintActionPendingId)}
                    sx={{ textTransform: "none" }}
                  >
                    Start Sprint
                  </Button>
                )}

                {canManageBoard && sprint.status === "paused" && (
                  <Button
                    size="small"
                    startIcon={<PlayArrowIcon fontSize="small" />}
                    onClick={(e) => { e.stopPropagation(); handleStartSprint(sprint); }}
                    disabled={Boolean(sprintActionPendingId)}
                    sx={{ textTransform: "none" }}
                  >
                    Resume Sprint
                  </Button>
                )}

                {canManageBoard && sprint.status === "active" && (
                  <Button
                    size="small"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                    onClick={(e) => { e.stopPropagation(); handleCompleteSprint(sprint); }}
                    disabled={Boolean(sprintActionPendingId)}
                    sx={{ textTransform: "none" }}
                  >
                    Complete Sprint
                  </Button>
                )}

                {canManageBoard && sprint.status === "completed" && (
                  <Button
                    size="small"
                    startIcon={<PlayArrowIcon fontSize="small" />}
                    onClick={(e) => { e.stopPropagation(); handleReopenSprint(sprint); }}
                    disabled={Boolean(sprintActionPendingId)}
                    sx={{ textTransform: "none" }}
                  >
                    Reopen Sprint
                  </Button>
                )}

                {canManageBoard && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditSprint(sprint); }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}

                {canManageBoard && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: "sprint", item: sprint }); }}
                  >
                    <Typography sx={{ fontSize: 12, color: "#dc2626" }}>✕</Typography>
                  </IconButton>
                )}
              </Box>

              <Collapse in={expandedSprints[sprint.sprint_id]}>
                <Box sx={{ px: 1, pb: 1 }}>
                  {(tasksBySprint[sprint.sprint_id] || []).filter(matchesBoardFilters).map((task) => (
                    <TaskRow
                      key={task.task_id}
                      task={task}
                      onEdit={openEditTask}
                      onDelete={(t) => setDeleteTarget({ type: "task", item: t })}
                      canManageBoard={canManageBoard}
                    />
                  ))}

                  {(tasksBySprint[sprint.sprint_id] || []).filter(matchesBoardFilters).length === 0 && (
                    <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", py: 1.5 }}>
                      {hasActiveBoardFilters ? "No tasks match the current filters" : "No tasks in this sprint yet"}
                    </Typography>
                  )}

                  {canCreateTasks && (
                    <Button
                      size="small"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => openCreateTask(sprint.sprint_id)}
                      sx={{ textTransform: "none", ml: 1 }}
                    >
                      Add Task
                    </Button>
                  )}
                </Box>
              </Collapse>
            </Box>
          ))}

          {sprints.length === 0 && (
            <EmptyState
              icon={DirectionsRunOutlinedIcon}
              title="No sprints yet"
              subtitle="Create one to start planning."
            />
          )}
        </Box>
      ) : activeTab === "backlog" ? (
        <Box>
          <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "text.primary" }}>
                Backlog
              </Typography>
              <Chip label={`${filteredBacklogTasks.length} tasks`} size="small" sx={{ height: 20, fontSize: 10 }} />
            </Box>

            <Box sx={{ px: 1, pb: 1 }}>
              {filteredBacklogTasks.map((task) => (
                <TaskRow
                  key={task.task_id}
                  task={task}
                  onEdit={openEditTask}
                  onDelete={(t) => setDeleteTarget({ type: "task", item: t })}
                  canManageBoard={canManageBoard}
                />
              ))}

              {filteredBacklogTasks.length === 0 && (
                <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", py: 1.5 }}>
                  {hasActiveBoardFilters ? "No tasks match the current filters" : "No tasks in the backlog"}
                </Typography>
              )}

              {canCreateTasks && (
                <Button
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => openCreateTask(null)}
                  sx={{ textTransform: "none", ml: 1 }}
                >
                  Add Task
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      ) : activeTab === "list" ? (
        <Box>
          <Box display="flex" flexWrap="wrap" gap={1.5} mb={2}>
            <TextField
              select
              size="small"
              value={listStatusFilter}
              onChange={(e) => setListStatusFilter(e.target.value)}
              displayEmpty
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {STATUS_COLUMNS.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "text.primary" }}>
              All Tasks
            </Typography>
            <Chip label={`${filteredListTasks.length} tasks`} size="small" sx={{ height: 20, fontSize: 10 }} />
          </Box>

          <TaskTable
            tasks={filteredListTasks}
            onEdit={openEditTask}
            onDelete={(t) => setDeleteTarget({ type: "task", item: t })}
            canManageBoard={canManageBoard}
          />
        </Box>
      ) : activeTab === "timeline" ? (
        <Box>
          {!timelineData ? (
            <EmptyState
              icon={TimelineOutlinedIcon}
              title="No sprint dates yet"
              subtitle="Add start/end dates to your sprints to see a timeline."
            />
          ) : (
            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  {new Date(timelineData.rangeStart).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  {new Date(timelineData.rangeEnd).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </Typography>
              </Box>

              {timelineData.rows.map(({ sprint, leftPct, widthPct, taskCount }) => (
                <Box key={sprint.sprint_id} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ width: 170, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }} noWrap>
                      {sprint.sprint_name}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                      {formatDateRange(sprint.start_date, sprint.end_date)}
                    </Typography>
                  </Box>

                  <Box sx={{ position: "relative", flex: 1, height: 28, bgcolor: "background.default", borderRadius: 1 }}>
                    <Tooltip title={`${sprint.sprint_name} \u00b7 ${taskCount} tasks`}>
                      <Box
                        onClick={() => canManageBoard && openEditSprint(sprint)}
                        sx={{
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          left: `${leftPct}%`,
                          width: `${widthPct}%`,
                          bgcolor: sprintTimelineColor(sprint.status),
                          borderRadius: 1,
                          cursor: canManageBoard ? "pointer" : "default",
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          "&:hover": canManageBoard ? { opacity: 0.85 } : {},
                        }}
                      >
                        <Typography sx={{ fontSize: 11, color: "#fff", fontWeight: 600 }} noWrap>
                          {taskCount} tasks
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : activeTab === "calendar" ? (
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>
              {CALENDAR_MONTH_NAMES[calendarMonth]} {calendarYear}
            </Typography>
            <Box display="flex" gap={0.5}>
              <IconButton size="small" onClick={() => shiftCalendarMonth(-1)}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => shiftCalendarMonth(1)}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }}>
            {CALENDAR_DAY_NAMES.map((d) => (
              <Typography key={d} align="center" sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", py: 0.5 }}>
                {d}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
            {calendarCells.map((cell, index) => {
              if (!cell) {
                return <Box key={`empty-${index}`} />;
              }

              const isToday = cell.date.toDateString() === new Date().toDateString();

              return (
                <Box
                  key={cell.day}
                  onClick={(e) => cell.daySprints.length > 0 && openCalendarDay(e, cell)}
                  sx={{
                    minHeight: 68,
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: isToday ? "primary.main" : "divider",
                    bgcolor: "background.paper",
                    p: 0.6,
                    cursor: cell.daySprints.length > 0 ? "pointer" : "default",
                    "&:hover": cell.daySprints.length > 0 ? { borderColor: "primary.main" } : {},
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: isToday ? 700 : 500, color: isToday ? "primary.main" : "text.secondary" }}>
                    {cell.day}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={0.3} mt={0.4}>
                    {cell.daySprints.slice(0, 2).map(({ sprint, color }) => (
                      <Box
                        key={sprint.sprint_id}
                        sx={{ fontSize: 9, px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: color, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {sprint.sprint_name}
                      </Box>
                    ))}
                    {cell.daySprints.length > 2 && (
                      <Typography sx={{ fontSize: 9, color: "text.secondary" }}>+{cell.daySprints.length - 2} more</Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Popover
            open={Boolean(calendarPopover)}
            anchorEl={calendarPopover?.anchorEl}
            onClose={() => setCalendarPopover(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 2, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>{calendarPopover?.dateLabel}</Typography>
              {calendarPopover?.sprints.map(({ sprint, color, taskCount }) => (
                <Box key={sprint.sprint_id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, flex: 1 }} noWrap>{sprint.sprint_name}</Typography>
                  <Chip label={`${taskCount} tasks`} size="small" sx={{ height: 18, fontSize: 10 }} />
                </Box>
              ))}
            </Box>
          </Popover>
        </Box>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          {boardSprintSections.length === 0 && (
            <EmptyState
              icon={ViewKanbanOutlinedIcon}
              title="No active sprint"
              subtitle="Start a sprint from the Sprints tab to see it here."
            />
          )}

          {boardSprintSections.map(({ sprint, columns, taskCount }) => (
            <Box key={sprint.sprint_id} sx={{ mb: 3 }}>
              <Box
                onClick={() => toggleSprintExpanded(sprint.sprint_id)}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, cursor: "pointer" }}
              >
                <IconButton size="small">
                  {expandedSprints[sprint.sprint_id] ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>

                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>
                  {sprint.sprint_name}
                </Typography>

                {sprint.status === "planned" && (
                  <Chip label="Planned" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "background.paper" }} />
                )}
                {sprint.status === "active" && (
                  <Chip label="In Progress" size="small" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                )}
                {sprint.status === "paused" && (
                  <Chip label="Paused" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#ffedd5", color: "#ea580c" }} />
                )}
                {sprint.status === "completed" && (
                  <Chip label="Completed" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                )}

                {formatDateRange(sprint.start_date, sprint.end_date) && (
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {formatDateRange(sprint.start_date, sprint.end_date)}
                  </Typography>
                )}

                <Chip label={`${taskCount} tasks`} size="small" sx={{ height: 20, fontSize: 10 }} />
              </Box>

              <Collapse in={expandedSprints[sprint.sprint_id]}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  {columns.map((column) => (
                    <Box
                      key={column.status}
                      sx={{ bgcolor: "background.default", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.2, display: "flex", flexDirection: "column" }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={1.2} px={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: statusAccent(column.status), flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", flex: 1 }} noWrap>
                          {column.status}
                        </Typography>
                        <Chip label={column.tasks.length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "background.paper" }} />
                      </Box>

                      <Droppable droppableId={`${sprint.sprint_id}::${column.status}`}>
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{ minHeight: 80, bgcolor: snapshot.isDraggingOver ? "action.hover" : "transparent", borderRadius: 1.5, p: 0.3 }}
                          >
                            {column.tasks.length === 0 && (
                              <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", py: 3 }}>
                                No tasks
                              </Typography>
                            )}

                            {column.tasks.map((task, index) => (
                              <BoardCard key={task.task_id} task={task} index={index} onEdit={openEditTask} canDrag={canCreateTasks} />
                            ))}

                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>

                      {canCreateTasks && (column.status === "To Do" || column.tasks.length === 0) && (
                        <Button
                          size="small"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => openCreateTask(sprint.sprint_id)}
                          sx={{ textTransform: "none", mt: 1, alignSelf: "flex-start" }}
                        >
                          Create Task
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          ))}
        </DragDropContext>
      )}

      <TaskDialog
        open={taskDialogOpen}
        task={editingTask}
        projectTeam={projectTeam}
        sprints={sprints}
        allTasks={tasks}
        defaultSprintId={defaultSprintId}
        isSaving={isSaving}
        currentEmployeeName={employee?.employee_name}
        currentEmployeeId={employee?.employee_id}
        fullScreen={Boolean(urlTaskId)}
        onClose={closeTaskDialog}
        onSave={handleSaveTask}
        onCreateSubtask={handleCreateSubtask}
        onEditSubtask={openEditTask}
      />

      <SprintDialog
        open={sprintDialogOpen}
        sprint={editingSprint}
        isSaving={isSaving}
        onClose={() => setSprintDialogOpen(false)}
        onSave={handleSaveSprint}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={isDeleting ? undefined : () => setDeleteTarget(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget?.type === "task"
              ? `Are you sure you want to delete "${deleteTarget?.item?.title}"?`
              : `Are you sure you want to delete "${deleteTarget?.item?.sprint_name}"? Its tasks will also be deleted.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ProjectBoard;
